/**
 * OtpInput
 *
 * Reusable OTP input made of single-character inputs. Supports:
 *  - configurable length
 *  - controlled and uncontrolled usage
 *  - paste handling, backspace navigation, arrow navigation
 *  - a single `onComplete` callback when the OTP becomes complete
 */
"use client";

import type React from "react";
import { useEffect, useRef, useState } from "react";

interface Props {
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  "aria-label"?: string;
}

const isDigit = (s: string) => /^[0-9]$/.test(s);

const OtpInput: React.FC<Props> = ({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled = false,
  autoFocus = false,
  "aria-label": ariaLabel = "otp-input",
}) => {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const [internalValue, setInternalValue] = useState<string>(value ?? "");
  const currentValue = value !== undefined ? value : internalValue;
  const digits = Array.from({ length }, (_, i) => currentValue[i] ?? "");

  const prevCompleteRef = useRef(false);

  useEffect(() => {
    const isComplete =
      currentValue.length === length && currentValue.split("").every(isDigit);
    if (onComplete && isComplete && !prevCompleteRef.current) {
      onComplete(currentValue);
    }
    prevCompleteRef.current = isComplete;
  }, [currentValue, length, onComplete]);

  const applyValue = (newVal: string) => {
    if (value === undefined) setInternalValue(newVal);
    onChange?.(newVal);
  };

  const handleChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const raw = e.target.value;
    const ch = raw.slice(-1);
    if (!ch) {
      const arr = currentValue.split("").slice(0, length);
      while (arr.length < length) arr.push("");
      arr[index] = "";
      applyValue(arr.join(""));
      return;
    }

    if (!isDigit(ch)) return;

    const newArr = currentValue.split("").slice(0, length);
    while (newArr.length < length) newArr.push("");
    newArr[index] = ch;
    applyValue(newArr.join(""));

    const next = inputsRef.current[index + 1];
    if (next) next.focus();
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    const key = e.key;
    const target = e.currentTarget;
    if (key === "Backspace") {
      if (target.value === "") {
        const prev = inputsRef.current[index - 1];
        if (prev) {
          prev.focus();
          const arr = currentValue.split("").slice(0, length);
          while (arr.length < length) arr.push("");
          arr[index - 1] = "";
          applyValue(arr.join(""));
        }
      }
    }
    if (key === "ArrowLeft") {
      const prev = inputsRef.current[index - 1];
      if (prev) prev.focus();
    }
    if (key === "ArrowRight") {
      const next = inputsRef.current[index + 1];
      if (next) next.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").trim();
    const chars = paste.split("").filter(isDigit).slice(0, length);
    if (chars.length === 0) return;
    const newVal = chars.concat(Array(length - chars.length).fill("")).join("");
    applyValue(newVal);
    const idx = Math.min(chars.length - 1, length - 1);
    const dest = inputsRef.current[idx];
    dest?.focus();
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: a fieldset would change visual layout — group role is sufficient here
    <div className="flex gap-2" role="group" aria-label={ariaLabel}>
      {Array.from({ length }).map((_, i) => (
        <input
          // biome-ignore lint/suspicious/noArrayIndexKey: OTP slots are positional and never reorder
          key={`otp-${i}-${length}`}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digits[i] ?? ""}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          ref={(el) => {
            inputsRef.current[i] = el;
          }}
          disabled={disabled}
          // biome-ignore lint/a11y/noAutofocus: OTP UX requires autofocus on first input
          autoFocus={autoFocus && i === 0}
          className="w-10 h-10 rounded border text-center text-lg focus:shadow-outline"
          aria-label={`${ariaLabel}-${i}`}
        />
      ))}
    </div>
  );
};

export default OtpInput;
